import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import Protocolo from "@/components/sections/Protocolo";
import Nosotros from "@/components/sections/Nosotros";
import Cotizar from "@/components/sections/Cotizar";
import { ARTICLES } from "@/data/articles";

const PageShell = ({ seo, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, []);
  return (
    <>
      <Seo
        title={seo.title}
        siteName="Fumiga"
        description={seo.meta}
        image="/fotos/compartir-og-1200x630.webp"
        jsonLd={seo.jsonLd}
      />
      <div className="pt-[72px]">{children}</div>
    </>
  );
};

export const CotizarPage = () => (
  <PageShell
    seo={{
      title: "Cotizá tu Aplicación por Hectárea | Fumiga",
      meta: "Decinos cultivo, hectáreas, zona y fecha. Te devolvemos el precio por hectárea el mismo día. También respondemos por WhatsApp.",
    }}
  >
    <Cotizar />
  </PageShell>
);

export const NosotrosPage = () => (
  <PageShell
    seo={{
      title: "Quiénes Somos | Fumiga Santa Cruz",
      meta: "Equipo de aplicación con drones en Santa Cruz: piloto formado, equipo asegurado y protocolo documentado. Conocé cómo trabajamos.",
    }}
  >
    <Nosotros />
    <CtaBand wa="Hola Fumiga, quiero conocer más sobre el equipo y cómo trabajan." />
  </PageShell>
);

export const ProtocoloPage = () => (
  <PageShell
    seo={{
      title: "Protocolo de Seguridad en Aplicación con Dron | Fumiga",
      meta: "Franjas de protección, seguro de responsabilidad civil, registro de vuelo por lote y ficha firmada. Así trabajamos, por escrito.",
    }}
  >
    <Protocolo />
    <CtaBand wa="Hola Fumiga, quiero ver el protocolo completo y cotizar." />
  </PageShell>
);

export const BlogPage = () => (
  <PageShell
    seo={{
      title: "Blog Agronómico y Normativa de Drones | Fumiga",
      meta: "Manejo de plagas, calendario de campaña y estado de la normativa de drones agrícolas en Bolivia. Escrito para el productor.",
    }}
  >
    <section className="relative bg-[#151A1F] pt-16 sm:pt-20 pb-20 overflow-hidden">
      <div className="absolute inset-0 hairlines" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 sm:px-10">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]">Blog agronómico</p>
        <h1 className="font-display font-bold tracking-tight leading-[1.06] text-3xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl">
          Manejo de plagas, calendario y normativa. Sin vueltas.
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#F6F7F5]/70 leading-relaxed">
          Escribimos para el productor, no para el algoritmo. Lo justo y necesario para
          decidir bien tu campaña.
        </p>
      </div>
    </section>
    <section className="bg-[#F6F7F5] text-[#151A1F] py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="border-t border-[#151A1F]/10">
          {ARTICLES.map((a, i) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`blog-article-${i}`}
              className="group border-b border-[#151A1F]/10 py-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10 hover:bg-white transition-colors duration-300"
            >
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#6B7280] w-24 shrink-0">{a.date}</span>
              <h2 className="font-display font-semibold text-lg sm:text-2xl tracking-tight flex-1 group-hover:text-[#046A38] transition-colors duration-300">{a.title}</h2>
              <div className="flex gap-2 shrink-0">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.15em] border border-[#151A1F]/15 rounded-full px-3 py-1 text-[#151A1F]/60">{a.tag}</span>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.15em] bg-[#046A38] rounded-full px-3 py-1 text-[#F6F7F5]">Publicado</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <CtaBand wa="Hola Fumiga, quiero que me avisen cuando salga el próximo artículo del blog." />
  </PageShell>
);

export const ArticlePage = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <NotFoundPage />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.meta,
    author: { "@type": "Organization", name: "Fumiga" },
    publisher: { "@type": "Organization", name: "Fumiga" },
    inLanguage: "es",
  };

  return (
    <PageShell
      seo={{
        title: `${article.title} | Fumiga`,
        meta: article.meta,
        jsonLd,
      }}
    >
      <section className="relative bg-[#151A1F] pt-16 sm:pt-20 pb-16 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 hairlines" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-6 sm:px-10">
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]">
            {article.tag} · {article.date}
          </p>
          <h1 className="font-display font-bold tracking-tight leading-[1.08] text-3xl sm:text-5xl mt-6">
            {article.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#F6F7F5]/70 leading-relaxed">
            {article.intro}
          </p>
        </div>
      </section>
      <section className="bg-[#F6F7F5] text-[#151A1F] py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-6 sm:px-10">
          {article.sections.map((s) => (
            <article key={s.heading} className="mt-12 first:mt-0" data-testid={`article-section-${s.heading.toLowerCase().replace(/[^a-z]/g, "-").slice(0, 30)}`}>
              <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{s.heading}</h2>
              {s.body.map((p) => (
                <p key={p.slice(0, 40)} className="mt-4 text-base text-[#151A1F]/75 leading-relaxed">{p}</p>
              ))}
              {s.bullets && (
                <ul className="mt-6 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-base text-[#151A1F]/80">
                      <span className="mt-2 w-2 h-2 shrink-0 bg-[#00A651]" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
          <Link
            data-testid="article-back-link"
            to="/blog"
            className="mt-16 inline-block font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#046A38] hover:gap-3 transition-all"
          >
            ← Volver al blog
          </Link>
        </div>
      </section>
      <CtaBand wa={article.wa} />
    </PageShell>
  );
};

export const NotFoundPage = () => (
  <section className="relative bg-[#151A1F] min-h-screen flex items-center overflow-hidden">
    <div className="absolute inset-0 hairlines" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-6 sm:px-10 py-32">
      <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#00A651]">Error 404</p>
      <h1 className="font-display font-bold tracking-tight text-4xl sm:text-6xl mt-6">Este lote no existe.</h1>
      <p className="mt-6 text-[#F6F7F5]/60 max-w-md">La página que buscás no está sembrada. Volvé al inicio y seguimos.</p>
      <Link
        data-testid="not-found-home-link"
        to="/"
        className="mt-10 inline-block bg-[#FFB000] text-[#151A1F] font-display font-semibold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform duration-300"
      >
        Volver al inicio
      </Link>
    </div>
  </section>
);
